import { Reveal, SectionTag } from "./Reveal";
import { DollarSign, Cog, Sparkles, UserMinus, LayoutGrid, Headset } from "lucide-react";

const features = [
  {
    icon: DollarSign,
    title: "Revenue Generation",
    body: "Multiple income streams: hourly rentals, F&B upsells, corporate events, leagues & tournaments.",
  },
  {
    icon: Cog,
    title: "Turnkey Automation",
    body: "Fully automated booking, access control, and hardware activation via SimBook integration.",
  },
  {
    icon: Sparkles,
    title: "Immersive Experience",
    body: "Multi-sport simulation with stunning visuals that keep customers coming back.",
  },
  {
    icon: UserMinus,
    title: "Low Staffing Costs",
    body: "Self-service kiosks, digital waivers, and smart-access PINs reduce labor overhead.",
  },
  {
    icon: LayoutGrid,
    title: "Flexible Configurations",
    body: "Modular bay designs that fit any space — from single bays to full entertainment centers.",
  },
  {
    icon: Headset,
    title: "Ongoing Support",
    body: "Dedicated onboarding, training, and 24/7 technical support to keep you running.",
  },
];

export default function Glance() {
  return (
    <section id="systems" data-testid="systems-section" className="py-28 md:py-40 bg-[#0A0A12] border-y border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-end mb-20">
          <Reveal>
            <SectionTag number="02" label="Systems at a Glance" />
            <h2 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
              Everything your facility needs to <span className="text-[#00D4FF]">succeed.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-white/55 leading-relaxed max-w-md lg:ml-auto">
              A complete sports simulation system — hardware, software,
              automation and support — engineered around your space.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06} className="bg-[#0A0A12]">
              <div data-testid={`system-card-${i}`} className="p-8 md:p-10 h-full hover:bg-[#0d0d18] transition-colors duration-300 group">
                <div className="h-12 w-12 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center group-hover:bg-[#007AFF]/20 transition-colors duration-300 mb-7">
                  <f.icon size={20} className="text-[#00D4FF]" />
                </div>
                <h3 className="font-heading font-bold tracking-tight text-xl group-hover:text-[#00D4FF] transition-colors duration-300">
                  {f.title}
                </h3>
                <p className="mt-4 text-sm text-white/50 leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="trusted-banner" className="mt-px relative overflow-hidden group">
            <img
              src="https://images.pexels.com/photos/6241298/pexels-photo-6241298.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Modern sports bar venue"
              className="w-full h-[320px] md:h-[380px] object-cover group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#05050A]/90 via-[#05050A]/50 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center p-10 md:p-16">
              <span className="font-mono-pv text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-4">Trusted Across North America</span>
              <p className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl max-w-2xl">
                Entertainment centers. Cinemas. Sports bars.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
